/**
 * AlertStatusControls - operator statuses on an alert (agion/Agion#2357)
 *
 * "I'm on it", "Mitigated" and "Resolved" are the same statuses the Slack
 * incident message shows; the alert store in analytics-service holds them and
 * a change here also updates the Slack message. A resolved (closed) alert
 * refuses the other statuses until it is reopened.
 */

import { useTranslation } from 'react-i18next'
import { Button, Text, SimpleTooltip } from '@agion/ui'
import { Check, CheckCircle2, ShieldCheck, CircleCheckBig, RotateCcw } from 'lucide-react'
import { formatRelativeTime } from '@/core/infra_utils'
import type { ActiveAlert } from '@/core/infra_types'
import type { AlertStatusAction } from './infra_monitoring.service'
import { alertOperatorStatuses } from './alert_helpers'

interface AlertStatusSummaryProps {
  alert: ActiveAlert
}

/** Who is on the alert and in what state, newest facts from the store. */
export function AlertStatusSummary({ alert }: AlertStatusSummaryProps) {
  const { t } = useTranslation(['pages'])
  const statuses = alertOperatorStatuses(alert)

  return (
    <div role="group" aria-label={t('pages:monitoring.alertDetail.operatorStatus.title', 'Status')} className="space-y-0.5">
      {statuses.length === 0 ? (
        <Text size="sm" color="muted">
          {t('pages:monitoring.alertDetail.operatorStatus.open', 'Open, nobody is on it yet')}
        </Text>
      ) : (
        statuses.map(status => (
          <div key={status.key} className="flex items-baseline gap-2">
            <Text size="sm">
              {t(`pages:monitoring.alertDetail.operatorStatus.${status.key}`, { by: status.by })}
            </Text>
            {status.at && (
              <Text size="xs" color="muted">{formatRelativeTime(status.at)}</Text>
            )}
          </div>
        ))
      )}
    </div>
  )
}

interface AlertStatusButtonsProps {
  alert: ActiveAlert
  isMutating: boolean
  onSetStatus: (alertId: string, status: AlertStatusAction) => void
  onClearStatus: (alertId: string, status: AlertStatusAction) => void
}

/** Toggle buttons for the three statuses; a pressed button clears its status. */
export function AlertStatusButtons({ alert, isMutating, onSetStatus, onClearStatus }: AlertStatusButtonsProps) {
  const { t } = useTranslation(['pages'])
  const isClosed = Boolean(alert.closedAt)

  const toggles = [
    {
      status: 'ack' as const,
      active: Boolean(alert.ackedBy),
      label: t('pages:monitoring.alerts.ack', "I'm on it"),
      clearLabel: t('pages:monitoring.alerts.unack', 'Remove "I\'m on it"'),
      Icon: Check,
    },
    {
      status: 'mitigate' as const,
      active: Boolean(alert.mitigatedBy),
      label: t('pages:monitoring.alerts.mitigate', 'Mitigated'),
      clearLabel: t('pages:monitoring.alerts.unmitigate', 'Remove "Mitigated"'),
      Icon: ShieldCheck,
    },
  ]
  const closedHint = t('pages:monitoring.alertDetail.operatorStatus.closedHint', 'Reopen the alert to change its status.')

  return (
    <>
      {toggles.map(({ status, active, label, clearLabel, Icon }) => (
        <SimpleTooltip key={status} content={isClosed ? closedHint : active ? clearLabel : label}>
          <Button
            variant={active ? 'default' : 'secondary'}
            size="sm"
            aria-pressed={active}
            onClick={() => active ? onClearStatus(alert.fingerprint, status) : onSetStatus(alert.fingerprint, status)}
            disabled={isMutating || isClosed}
            className="flex items-center gap-2"
          >
            {active ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Icon size={14} />}
            {label}
          </Button>
        </SimpleTooltip>
      ))}

      <SimpleTooltip content={t('pages:monitoring.alerts.closeHint', 'Mark the incident resolved. The Slack message updates too.')}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => isClosed ? onClearStatus(alert.fingerprint, 'close') : onSetStatus(alert.fingerprint, 'close')}
          disabled={isMutating}
          className="flex items-center gap-2"
        >
          {isClosed ? <RotateCcw size={14} /> : <CircleCheckBig size={14} />}
          {isClosed ? t('pages:monitoring.alerts.reopen', 'Reopen') : t('pages:monitoring.alerts.close', 'Resolved')}
        </Button>
      </SimpleTooltip>
    </>
  )
}
